import { getCenter } from "ol/extent";
import { WKT } from "ol/format";
import { RIMSPolyline } from "../types/Polylines";
import { TreeNodeData } from "@mantine/core";
import { Polygon } from "../types/Polygon";
import { Point } from "../types/Point";


/**
 * Generates a random RGB color code.
 * @returns {number[]} The randomly generated color code as an array of RGB values.
 */
export function getRandomColourRGB(): { r: number; g: number; b: number; a: number } {
	const r = Math.floor(Math.random() * 256);
	const g = Math.floor(Math.random() * 256);
	const b = Math.floor(Math.random() * 256);

	return { r, g, b, a: 1 };
}


/**
 * Converts a hexadecimal color code to an RGBA color value.
 *
 * @param hex - The hexadecimal color code as a string. It can be in the format "RRGGBB" or "RGB" or "RRGGBBAA" or "RGBA".
 * @returns An object containing the RGBA values as numbers, or `{ r: 0, g: 0, b: 0, a: 0.4 }` if the input is invalid.
 *
 * @example
 * ```typescript
 * const rgba = hexToRGBA("#03F"); // Returns { r: 0, g: 51, b: 255, a: 1 }
 * const rgba = hexToRGBA("0033FF"); // Returns { r: 0, g: 51, b: 255, a: 1 }
 * const rgba = hexToRGBA("0033FF80"); // Returns { r: 0, g: 51, b: 255, a: 0.5 }
 * const rgba = hexToRGBA("invalid"); // Returns { r: 0, g: 0, b: 0, a: 0.4 }
 * ```
 */
export function hexToRGBA(
	hex: string,
	defaultTransparency: number = 1
): { r: number; g: number; b: number; a: number } {
	hex = hex.replace(/^#/, '');

	const defaultReturn = { r: 0, g: 0, b: 0, a: 0.4 };

	if (!/^[0-9A-Fa-f]{3,8}$/.test(hex)) {
		return defaultReturn;
	}

	if (hex.length === 3) {
		hex = hex
			.split('')
			.map((char) => char + char)
			.join('');
	}

	if (hex.length === 4) {
		hex = hex
			.split('')
			.map((char, index) => (index < 3 ? char + char : char)) //expand rgb
			.join('');
	}

	const bigint = parseInt(hex, 16);
	let r: number, g: number, b: number, a: number;

	if (hex.length > 6) {
		// 8-digit hex: RRGGBBAA
		r = (bigint >> 24) & 255;
		g = (bigint >> 16) & 255;
		b = (bigint >> 8) & 255;
		a = (bigint & 255) / 255;
	} else {
		// 6-digit hex: RRGGBB
		r = (bigint >> 16) & 255;
		g = (bigint >> 8) & 255;
		b = bigint & 255;
		a = defaultTransparency;
	}

	return { r, g, b, a };
}


/**
 * Calculates the center coordinates of a geometry represented by a WKT (Well-Known Text) string.
 *
 * @param wktString - The WKT string representing the geometry.
 * @returns A tuple containing the center coordinates [longitude, latitude] or null if an error occurs.
 *
 * @example
 * ```typescript
 * const wkt = 'POLYGON((30 10, 40 40, 20 40, 10 20, 30 10))';
 * const center = GetCentreCoordinates(wkt);
 * ```
 */
export function GetCentreCoordinates(wktString: string): [number, number] | null {
	try {
		//Parse the WKT string to an OpenLayers geometry
		const format = new WKT();
		const geometry = format.readGeometry(wktString, {
			dataProjection: 'EPSG:3067',
			featureProjection: 'EPSG:3857',
		});

		//Get the extent of the geometry (bounding box)
		const extent = geometry.getExtent();

		//Get the centre of the extent (geometry's bounding box)
		const centerCoordinates = getCenter(extent);

		return centerCoordinates as [number, number]; //Returns the centre coordinates
	} catch (error) {
		console.error('Error calculating area from WKT:', error);
		return null; //Return null in case of an error
	}
}


export function instantiateLegendTree() {
    var treeData: TreeNodeData[] = [];
    
    // Add Example Static Layer with status children
    const statusNodes: TreeNodeData[] = [
        {
            label: "Preliminary",
            value: "rims-preset-node-status-preliminary",
            nodeProps: {
                "visible": true,
                "statusColor": "grey"
            }
        },
        {
            label: "Planned",
            value: "rims-preset-node-status-planned",
            nodeProps: {
                "visible": true,
                "statusColor": "yellow"
            }
        },
        {
            label: "Given to the Contractor",
            value: "rims-preset-node-status-contractor",
            nodeProps: {
                "visible": true,
                "statusColor": "red"
            }
        },
        {
            label: "Ready",
            value: "rims-preset-node-status-completed",
            nodeProps: {
                "visible": true,
                "statusColor": "green"
            }
        },
        {
            label: "Approved",
            value: "rims-preset-node-status-approved",
            nodeProps: {
                "visible": true,
                "statusColor": "blue"
            }
        },
        {
            label: "Others",
            value: "rims-preset-node-status-others",
            nodeProps: {
                "visible": true,
                "statusColor": "black"
            }
        }
    ];

    // Add User Geometries category node
    treeData.push({
        label: "User Geometries",
        value: "rims-preset-node-user-geometries",
        nodeProps: {
            "visible": true
        },
        // children: statusNodes
    });

    treeData.push({
        label: "Example Static Layer",
        value: "rims-preset-node-static-layer",
        nodeProps: {
            "visible": true
        },
        children: statusNodes
    });

    return treeData;
}

export function getNameInitials(name: string | undefined | null): string {
    if (!name) return "";

    const words = name.trim().split(/\s+/).filter(word => word.length > 0);

    if (words.length === 0) return "";
    if (words.length === 1) return words[0][0].toUpperCase();

    // Get first letter of first word and first letter of last word
    const firstInitial = words[0][0].toUpperCase();
    const lastInitial = words[words.length - 1][0].toUpperCase();

    return firstInitial + lastInitial;
}

// =============================================================================
// Diary Entry Working Group / Equipment Multi-Select Utilities
// =============================================================================

const ITEM_DELIM = "\n";

/**
 * Parses a multi-item string from diary entry fields (workingGroup/equipment).
 * Auto-migrates legacy comma-separated values by preferring newline, falling
 * back to comma if no newlines exist. De-duplicates to prevent Mantine
 * MultiSelect crashes on legacy rows where comma-split created duplicates.
 */
export const parseDiaryItemList = (raw: string | null | undefined): string[] => {
  if (!raw) return [];
  const delim = raw.includes("\n") ? "\n" : ",";
  const trimmed = raw
    .split(delim)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  return Array.from(new Set(trimmed));
};

/**
 * Serializes an array of diary items into a single delimited string.
 */
export const serializeDiaryItemList = (items: string[]): string => items.join(ITEM_DELIM);

/**
 * Parses a string to an optional integer. Returns null if the value is
 * null/undefined/empty or not a valid number.
 */
export const parseOptionalInt = (val?: string | null): number | null => {
  if (!val) return null;
  const n = parseInt(val, 10);
  return Number.isNaN(n) ? null : n;
};